/**
 * Modal de confirmación / aviso
 * Sigue el patrón de modales del proyecto (ver peticiones/ModalEliminar)
 */
import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Stack,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Modal de confirmación / aviso.
 * Con onConfirm se comporta como confirmación (Cancelar + acción); sin él, como
 * aviso informativo con un único botón de cierre.
 * `detalles` pinta el recuadro gris de pares etiqueta/valor del patrón.
 * `children` se pinta al final del contenido, para opciones extra (una casilla, por ejemplo).
 */
const ModalConfirmacion = ({
    show,
    onHide,
    onConfirm,
    loading = false,
    title = "Aviso",
    titleMobile,
    text = "",
    detalles = [],
    confirmLabel = "Confirmar",
    confirmLabelMobile,
    cancelLabel = "Cancelar",
    icon: Icon = WarningIcon,
    iconColor = "#cc071e",
    confirmIcon: ConfirmIcon = CheckIcon,
    children
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const esConfirmacion = Boolean(onConfirm);

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <Icon sx={{ color: iconColor, fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" }
                        }}
                    >
                        {isMobile && titleMobile ? titleMobile : title}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: "#6c757d",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)"
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial"
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: 14, sm: 15 },
                        color: "#495057",
                        lineHeight: 1.6
                    }}
                    dangerouslySetInnerHTML={{ __html: text }}
                />

                {detalles.length > 0 && (
                    <Box
                        sx={{
                            mt: { xs: 2, sm: 2.5 },
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: "#f8f9fa",
                            borderRadius: 2,
                            border: "1px solid #e9ecef"
                        }}
                    >
                        <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                            {detalles.map(({ label, value }) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: "flex-start",
                                        gap: { xs: 0.5, sm: 1 }
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            minWidth: { xs: "auto", sm: 80 }
                                        }}
                                    >
                                        {label}:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            color: "#212529",
                                            flex: 1,
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {value || "-"}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                {children}
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                {esConfirmacion && (
                    <Button
                        onClick={onHide}
                        disabled={loading}
                        variant="outlined"
                        fullWidth={isMobile}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: { xs: 14, sm: 15 },
                            color: "#6c757d",
                            borderColor: "#6c757d",
                            "&:hover": {
                                borderColor: "#495057",
                                backgroundColor: "rgba(0,0,0,0.04)"
                            }
                        }}
                    >
                        {cancelLabel}
                    </Button>
                )}
                <Button
                    onClick={esConfirmacion ? onConfirm : onHide}
                    disabled={loading}
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                            <ConfirmIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#cc071e",
                        "&:hover": {
                            backgroundColor: "#a30618"
                        },
                        "&:disabled": {
                            backgroundColor: "#6c757d",
                            color: "#fff"
                        }
                    }}
                >
                    {!esConfirmacion
                        ? "Aceptar"
                        : isMobile && confirmLabelMobile
                            ? confirmLabelMobile
                            : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalConfirmacion;
